import React, { useEffect, useMemo } from 'react';
import {
  FormFieldItem,
  Header,
  SetPassword,
  StyleProvider,
  defaultBrandingData,
} from '@relaynetwork/design-system';
import type { ProductGroup } from '@app/core/models/client';
import { i18nLocal } from '@relaynetwork/design-system';

// Preview-only no-op for the organism's submit handler (form submission lives
// in The Feed's set-password container, not the PDF preview).
const noop = (): void => undefined;

export type SetPasswordPagePreviewProps = {
  pswMessageText: ProductGroup['password']['label'];
  pswButtonLabel: ProductGroup['password']['label'];
  pswTCS: ProductGroup['password']['tcs'];
  pswTCMandatory: ProductGroup['password']['tc_mandatory'];
  logoUrl: ProductGroup['branding']['icon_s3_url'];
  color: ProductGroup['branding']['color'];
  showProductGroup: ProductGroup['hide_pg_wire_display_name'];
  productGroupName: ProductGroup['name'];
  productGroupDescription: ProductGroup['description'];
  fdicSignage: ProductGroup['fdic'];
  showFdic: boolean;
  language?: string;
  showLogo?: boolean;
  showTitleBar?: boolean;
  consentContent?: string;
  /** Renders the set-password error banner (DS `isSetPasswordError`). */
  isError?: boolean;
  /**
   * Product group es overrides (`i18n.es`). When provided (PDF-preview path),
   * they are registered as the DS i18next `es` bundle so the `SetPassword`
   * organism resolves `translations.password.*` / `translations.consent.*`
   * itself, applying fallbackLng: 'en' over the base values passed above.
   * The config/translations editors omit it — they drive the organism from
   * live form values and rely on prop-verbatim rendering.
   */
  i18nEs?: NonNullable<ProductGroup['i18n']>['es'];
};

export default function SetPasswordPagePreview({
  pswMessageText,
  pswButtonLabel,
  pswTCS,
  pswTCMandatory,
  logoUrl,
  color,
  showProductGroup,
  productGroupName,
  productGroupDescription,
  fdicSignage,
  showFdic,
  language = 'en',
  showLogo = true,
  showTitleBar = true,
  consentContent,
  isError = false,
  i18nEs,
}: SetPasswordPagePreviewProps) {
  const fields: FormFieldItem[] = [
    {
      field: 'password',
      prompt: language === 'es' ? 'Contraseña' : 'Password',
      type: 'password',
      alwaysShowHints: true,
      required: true,
      upperCaseRequired: true,
      lowerCaseRequired: true,
      numberRequired: true,
      minimumLength: 8,
      maximumLength: 256,
    },
    {
      field: 'confirm_password',
      prompt: language === 'es' ? 'Confirmar Contraseña' : 'Confirm Password',
      type: 'password',
      required: true,
      equalityCheckField: 'password',
    },
  ];

  const brandingData = useMemo(
    () => ({
      ...defaultBrandingData,
      color: color,
    }),
    [color],
  );

  // Initialize i18n, register the product group's es overrides (so the DS
  // organism resolves them via fallbackLng: 'en'), then set the language.
  // `init()` resets resources to the DS defaults, so the bundle must be added
  // after it and before changeLanguage (same order as WirePreview).
  useEffect(() => {
    i18nLocal.init();
    if (i18nEs) {
      i18nLocal.addResourceBundle('es', 'common', i18nEs, true, true);
    }
    i18nLocal.changeLanguage(language);

    return () => {
      i18nLocal.init();
      i18nLocal.changeLanguage('en');
    };
  }, [language, i18nEs]);

  return (
    <StyleProvider brandingData={brandingData}>
      <Header
        productGroupDescription={productGroupDescription}
        productGroupName={showProductGroup ? productGroupName : ' '}
        showTitleNavBar={showTitleBar}
        logoUrl={logoUrl}
        showRegularNavBar
        showLogo={showLogo}
        showBanner={false}
        isPreview={true}
        isMobile
        isPortrait={true}
        fdic={fdicSignage}
        showFDICSignageOnPassword={showFdic}
      />
      <div
        style={{
          marginTop: '1rem',
          marginLeft: '0',
          marginRight: '0',
        }}
      >
        <SetPassword
          isMobile={false}
          fields={fields}
          onSubmit={noop}
          pswButtonLabel={pswButtonLabel}
          pswTCS={pswTCS}
          consentTSCS={consentContent}
          pswMessageText={pswMessageText ?? ''}
          pswTCMandatory={pswTCMandatory}
          consentType="express"
          isSetPasswordError={isError}
        />
      </div>
    </StyleProvider>
  );
}
